import { IconUser } from '@tabler/icons-react';

import { useEffect, useMemo, useState } from 'react';

import { Badge, Group, Paper, SegmentedControl, Text } from '@mantine/core';

import { useGetApiNotionProjectProjectIdUserStories } from '../../../../api/notion/notion';
import { useGetApiNotionPhasePhaseIdUserStories } from '../../../../api/notion/notion';
import { useDocumentContext } from '../contexts/DocumentContext';

type MvpFilter = 'all' | 'mvp' | 'non-mvp';

export interface UserStoriesData {
    mvpFilter: MvpFilter;
}

interface UserStoriesRendererProps {
    data: UserStoriesData;
    onChange: (data: UserStoriesData) => void;
}

export function UserStoriesRenderer({
    data,
    onChange
}: UserStoriesRendererProps) {
    const { document } = useDocumentContext();
    const projectId = document.projectId;
    const phaseId = document.notionPhaseId;
    const [mvpFilter, setMvpFilter] = useState<MvpFilter>(
        data?.mvpFilter ?? 'all'
    );

    // Sync from external data changes
    useEffect(() => {
        if (data?.mvpFilter && data.mvpFilter !== mvpFilter) {
            setMvpFilter(data.mvpFilter);
        }
    }, [data?.mvpFilter]);

    const handleMvpFilterChange = (value: string) => {
        const newFilter = value as MvpFilter;
        setMvpFilter(newFilter);
        onChange({ ...data, mvpFilter: newFilter });
    };

    // Use phase user stories if document is associated with a phase
    const {
        data: projectUserStories,
        isLoading: projectLoading,
        error: projectError
    } = useGetApiNotionProjectProjectIdUserStories(
        projectId,
        undefined, // No filtering parameters
        {
            query: { enabled: !!projectId && !phaseId } // Only fetch if no phase ID
        }
    );

    const {
        data: phaseUserStories,
        isLoading: phaseLoading,
        error: phaseError
    } = useGetApiNotionPhasePhaseIdUserStories(
        phaseId ?? '',
        undefined, // No filtering parameters
        {
            query: { enabled: !!phaseId } // Only fetch if phase ID exists
        }
    );

    // Use phase data if available, otherwise fall back to project data
    const allUserStories = phaseId ? phaseUserStories : projectUserStories;
    const isLoading = phaseId ? phaseLoading : projectLoading;
    const error = phaseId ? phaseError : projectError;

    // Filter by MVP status
    const userStories = useMemo(() => {
        if (!allUserStories) return allUserStories;
        if (mvpFilter === 'mvp') return allUserStories.filter(s => s.mvp);
        if (mvpFilter === 'non-mvp')
            return allUserStories.filter(s => !s.mvp);
        return allUserStories;
    }, [allUserStories, mvpFilter]);

    // Calculate summary data from real user stories
    const summaryData =
        userStories && userStories.length > 0
            ? (() => {
                  const totalMin = userStories.reduce(
                      (sum, story) => sum + (story.effortMin ?? 0),
                      0
                  );
                  const totalMax = userStories.reduce(
                      (sum, story) => sum + (story.effortMax ?? 0),
                      0
                  );
                  const mean = Math.round((totalMin + totalMax) / 2);
                  const variance = Math.round(Math.abs(mean - totalMin)); // or Math.abs(mean - totalMax), they're the same

                  return {
                      count: userStories.length.toString(),
                      totalEstimate:
                          variance > 0
                              ? `${mean} ±${variance}`
                              : mean.toString(),
                      topPriorities: userStories.slice(0, 3).map(story => {
                          // Use the goal column
                          const storyName = story.goal ?? 'Untitled Story';
                          return storyName.length > 50
                              ? `${storyName.substring(0, 47)}...`
                              : storyName;
                      })
                  };
              })()
            : {
                  count: userStories ? userStories.length.toString() : '?',
                  totalEstimate: userStories ? '0' : '?',
                  topPriorities: isLoading
                      ? ['Loading user stories...']
                      : error
                        ? ['Error loading user stories']
                        : userStories
                          ? ['No user stories in project']
                          : ['Loading...']
              };

    return (
        <Paper
            p='md'
            withBorder
            radius='md'
            style={{
                margin: '1rem 0',
                backgroundColor:
                    'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))'
            }}
        >
            <Group gap='sm' mb='xs' justify='space-between'>
                <Group gap='sm'>
                    <IconUser size={20} color='var(--mantine-color-blue-6)' />
                    <Text fw={600} size='sm'>
                        User Stories Summary
                    </Text>
                    <Badge variant='filled' size='sm'>
                        {summaryData.count}{' '}
                        {mvpFilter === 'mvp'
                            ? 'MVP stories'
                            : mvpFilter === 'non-mvp'
                              ? 'Non-MVP stories'
                              : 'stories'}
                    </Badge>
                </Group>
                <SegmentedControl
                    size='xs'
                    value={mvpFilter}
                    onChange={handleMvpFilterChange}
                    data={[
                        { label: 'All', value: 'all' },
                        { label: 'MVP', value: 'mvp' },
                        { label: 'Non-MVP', value: 'non-mvp' }
                    ]}
                />
            </Group>

            <Group gap='md' mb='xs'>
                <Text size='xs' c='dimmed'>
                    Total Estimate: <strong>{summaryData.totalEstimate}</strong>{' '}
                    effort days
                </Text>
                {mvpFilter === 'all' &&
                    allUserStories &&
                    allUserStories.length > 0 && (
                        <>
                            <Text size='xs' c='dimmed'>
                                MVP:{' '}
                                <strong>
                                    {
                                        allUserStories.filter(s => s.mvp)
                                            .length
                                    }
                                </strong>{' '}
                                stories
                            </Text>
                            <Text size='xs' c='dimmed'>
                                Non-MVP:{' '}
                                <strong>
                                    {
                                        allUserStories.filter(s => !s.mvp)
                                            .length
                                    }
                                </strong>{' '}
                                stories
                            </Text>
                        </>
                    )}
            </Group>

            <div>
                <Text size='xs' c='dimmed' mb={4}>
                    Top Stories:
                </Text>
                {summaryData.topPriorities.map((title, index) => (
                    <Text
                        key={index}
                        size='xs'
                        c='dimmed'
                        style={{ marginLeft: '8px' }}
                    >
                        • {title}
                    </Text>
                ))}
            </div>

            <Text size='xs' c='dimmed' mt='xs' fs='italic'>
                Sourced from the linked Notion{' '}
                {document.notionPhaseId ? 'project phase' : 'project'} page.
                Full details will be included in exported documents.
            </Text>
        </Paper>
    );
}
